import { useEffect, useState } from 'react';
import { endAt, get, orderByKey, query, ref, startAt } from 'firebase/database';
import { db } from '../../firebase';
import type { ActivityEntry } from '../../types/activity';
import { ActorInfo, parseActivity } from '../../Pages/Superadmin/UserActivity/userActivityView';

interface UseUserActivityReturn {
    entries: ActivityEntry[];
    actors: Map<string, ActorInfo>;
    buildingNames: Map<string, string>;
    loading: boolean;
    error: string | null;
}

function parseActors(raw: unknown): Map<string, ActorInfo> {
    const actors = new Map<string, ActorInfo>();
    if (!raw || typeof raw !== 'object') return actors;
    for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
        if (!value || typeof value !== 'object') continue;
        const v = value as Record<string, unknown>;
        actors.set(id, {
            id,
            name: typeof v.name === 'string' && v.name ? v.name : 'Okänd användare',
            superadmin: v.superadmin === true,
            external: v.isExternal === true,
            deleted: v.deleted === true,
        });
    }
    return actors;
}

function parseBuildingNames(raw: unknown): Map<string, string> {
    const names = new Map<string, string>();
    if (!raw || typeof raw !== 'object') return names;
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        const name = value && typeof value === 'object' ? (value as { name?: unknown }).name : undefined;
        if (typeof name === 'string' && name) names.set(key, name);
    }
    return names;
}

/**
 * Användaraktivitet data (superadmin only): the log for `startDate..endDate`
 * (date keys, inclusive), the actors from `users/userList` and building names.
 */
export const useUserActivity = (startDate: string, endDate: string): UseUserActivityReturn => {
    const [entries, setEntries] = useState<ActivityEntry[]>([]);
    const [actors, setActors] = useState<Map<string, ActorInfo>>(new Map());
    const [buildingNames, setBuildingNames] = useState<Map<string, string>>(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const [activitySnap, usersSnap, buildingsSnap] = await Promise.all([
                    get(query(ref(db, 'activity'), orderByKey(), startAt(startDate), endAt(endDate))),
                    get(ref(db, 'users/userList')),
                    get(ref(db, 'buildingsSummary')),
                ]);
                if (cancelled) return;
                setEntries(parseActivity(activitySnap.val()));
                setActors(parseActors(usersSnap.val()));
                setBuildingNames(parseBuildingNames(buildingsSnap.val()));
            } catch (err) {
                console.error('Kunde inte läsa användaraktivitet:', err);
                if (!cancelled) setError('Användaraktiviteten kunde inte läsas. Försök igen.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    return { entries, actors, buildingNames, loading, error };
};
